import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { disabled, FieldTree, form, max, min, required } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { MatDialog } from '@angular/material/dialog'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { KayttajanTiedot, TositteenLisaysprosessinOsanTunniste } from '@lemon/core-firebase/model/kayttaja'
import {
  FirestoreTosite,
  FirestoreTositteenAlkuperainenTiedosto,
  FirestoreTositteenKuva,
  MYYNTITOSITE_MAKSUTAPA_ID,
  PALKKATOSITE_MAKSUTAPA_ID,
  SAHKOISET_LASKUT_MAKSUTAPA_ID,
  TILIOTETOSITE_MAKSUTAPA_ID,
} from '@lemon/core-firebase/model/tosite'
import { WebTositeTallennaPyynto, WebTositeTallennaVastaus } from '@lemon/core-firebase/model/web-tosite-api'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { LocalMonth } from '@lemon/core/model/common'

import { TositeMuokkaaComponentData } from '../../_angular/_resolvers/tosite.resolve'
import { KayttajaService } from '../../_angular/service/kayttaja.service'
import { PurettuTiedosto, TositeService } from '../../_angular/service/tosite/tosite.service'
import { TositeLukittuDialog } from '../tosite-lukittu.dialog'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { TositeDatasourceUusiService } from '../../_angular/service/tosite/tosite-datasource-uusi.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidators } from '../../_jaettu-angular/_validators/FormValidators'
import { ProjektitService } from '../../_jaettu-angular/projektit.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { SimplifiedUploadData, TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'
import { TositeKuvaCacheService } from '../kuvat/tosite-kuva-cache.service'
import { luoUusiToimittajaLomake, UusiToimittajaLomake, uusiToimittajaLomakkeesta } from '../lisaa-tositteita/toimittaja-kentta/toimittaja-kentta.component'

import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { BehaviorSubject, Observable, Subject, combineLatest, of as observableOf } from 'rxjs'
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import { kutsuTositteenTallennusta } from '../tositteen-tallennuskutsu'

interface TositeLomake {
  maksutapa: number | null
  tositteenPvm: Date | null
  summa: number | null
  toimittajanNimi: string
  toimittajaAvain: string | null
  projektiAvain: string
  lisatiedot: string
  alvVahennysoikeus: number | null
}

interface MuokattavaSivu {
  alkuperaisenAvain: string
  nimi: string
  esikatselukuva: string
  kuvaUrlit: string[]
  kuvaAvaimet: string[]
  uploadData: SimplifiedUploadData | null
  uusi: boolean
  lukittu: boolean
}

@Component({
  templateUrl: './muokkaa-tositetta.component.html',
  styleUrls: ['./muokkaa-tositetta.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }],
  standalone: false,
})
export class MuokkaaTositettaComponent implements OnInit, OnDestroy {
  maxDate: Date = new Date(2099, 11, 31)
  minDate: Date = new Date(2010, 0, 1)

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>

  private ngUnsubscribe = new Subject<void>()

  private _uploadTasksSubject: BehaviorSubject<SimplifiedUploadData[]> = new BehaviorSubject([])
  private _sivutSubject = new BehaviorSubject<MuokattavaSivu[]>([])
  private _nykyinenSivuIndeksiSubject = new BehaviorSubject<number>(0)

  lomake: FieldTree<TositeLomake> = null
  maksutapa: FieldTree<number | null>
  tositteenPvm: FieldTree<Date | null>
  summa: FieldTree<number | null>
  toimittajanNimi: FieldTree<string>
  toimittajaAvain: FieldTree<string | null>
  // Toimittajakentän uuden toimittajan lomake on toimittajakentän reaktiivinen sopimus, jonka sen yksikkötestit lukitsevat
  uusiToimittaja: FormGroup<UusiToimittajaLomake>
  projektiAvain: FieldTree<string>
  lisatiedot: FieldTree<string>
  alvVahennysoikeus: FieldTree<number | null>

  private readonly _injector = inject(Injector)
  private readonly _maksutapaPoissa = signal(false)
  private readonly _tositteenPvmPoissa = signal(false)
  private readonly _summaPoissa = signal(false)
  private readonly _alvVahennysoikeusPoissa = signal(false)

  kuvavirheAvain: string = ''
  kuvavirheParametrit: Object = {}

  hideSum: boolean = false
  showPvm: boolean = true
  dateSelectionType: 'day' | 'month' = 'day'
  pvmText: string
  summaText: string

  kuitti: FirestoreTosite = null
  alvPortaat: number[] = null
  virheviestiLatausOnKesken: string = null
  virheviestiPysyva: string = null
  virheviestiTallennus: string = null
  virheviestiEiKuvia: string = null
  tallennusEstyi = false
  showMaksutavat: boolean = true

  naytaProjektitObservable = this._projektitService.naytaProjektitObservable
  naytaModal = false

  sivutObservable: Observable<MuokattavaSivu[]>
  nykyinenSivuObservable: Observable<MuokattavaSivu | null>
  sivujenMaaraObservable: Observable<number>

  private newAlkuperaisetCache: FirestoreTositteenAlkuperainenTiedosto[] = []
  private newKuvatCache: FirestoreTositteenKuva[] = []

  private kayttajanTiedot: KayttajanTiedot = null
  private onkoVielaSelvitettaviaListalla: boolean
  private _selvityspyynnonAvain: string | undefined
  private startingMaksutapa: number
  private startingMonth: LocalMonth

  alvPortaatObservable: Observable<{ portaat: number[]; oletusProsentti: number | null }>

  donePercentageObservable: Observable<number>
  latausvirheetObservable: Observable<string[]>
  kirjanpitajanKommentti: string
  maksajaTaiMaksunSaaja: string

  private oletusAlv: number = null
  private _poistetuksiMerkitytSivut: string[] = []

  get nykyinenSivuIndeksi(): number {
    return this._nykyinenSivuIndeksiSubject.value
  }
  set nykyinenSivuIndeksi(arvo: number) {
    this._nykyinenSivuIndeksiSubject.next(arvo)
  }

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _tositeService: TositeService,
    private _ladataanService: LadataanService,
    private _dateService: DateService,
    private _timestampService: TimestampService,
    private _currencyService: CurrencyService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _tositeDatasourceUusiService: TositeDatasourceUusiService,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _changeDetectionRef: ChangeDetectorRef,
    private _copyService: KopioijaPalvelu,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _projektitService: ProjektitService,
    private _dialog: MatDialog,
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
  ) {
    this.maxDate = this._dateService.lisaaKuukausia(new Date(), 12)
    this.minDate = this._dateService.lisaaKuukausia(new Date(), -48)
  }

  ngOnInit() {
    this.sivutObservable = this._sivutSubject.asObservable()
    this.sivujenMaaraObservable = this.sivutObservable.pipe(map(sivut => sivut.length))
    this.nykyinenSivuObservable = combineLatest([this.sivutObservable, this._nykyinenSivuIndeksiSubject]).pipe(
      map(([sivut, indeksi]) => {
        if (sivut.length === 0) {
          return null
        }
        const korjattuIndeksi = Math.min(indeksi, sivut.length - 1)
        return sivut[korjattuIndeksi]
      }),
    )

    const tositeDataObservable = this._route.data.pipe(map(routeData => (routeData?.data || null) as TositeMuokkaaComponentData))

    combineLatest([this._kayttajaService.kayttajanTiedotObservable, tositeDataObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ([kayttajanTiedot, data]) => {
          this.kayttajanTiedot = kayttajanTiedot
          this._poistetuksiMerkitytSivut = []

          this.onkoVielaSelvitettaviaListalla = data?.onkoVielaSelvitettavia
          this._selvityspyynnonAvain = data?.selvityspyynnonAvain
          this.startingMaksutapa = data.tosite.maksutapa
          this.startingMonth = { year: data.tosite.localPvm.year, month: data.tosite.localPvm.month }

          if (data?.tosite) {
            this.kuitti = this._copyService.cloneDeep(data.tosite)
            this._sivutSubject.next(this._rakennaSivutOlemassaolevasta(kayttajanTiedot, this.kuitti))

            this._maksutapaPoissa.set(false)
            this._tositteenPvmPoissa.set(false)
            this._summaPoissa.set(false)
            this._alvVahennysoikeusPoissa.set(false)
            this.lomake = form(
              signal<TositeLomake>({
                maksutapa: null,
                tositteenPvm: this.kuitti.p ? this._dateService.numberToDate(this.kuitti.p) : this._dateService.timestampToDate(this.kuitti.pvm),
                summa: this.kuitti.sum ?? this.kuitti.summa,
                toimittajanNimi: this.kuitti.seller ?? '',
                toimittajaAvain: this.kuitti.toimittajaAvain ?? null,
                projektiAvain: this.kuitti.projektiAvain ?? '',
                lisatiedot: this.kuitti.selite ?? '',
                alvVahennysoikeus: this.kuitti.alvVahennysoikeus,
              }),
              polku => {
                required(polku.maksutapa)
                disabled(polku.maksutapa, () => this._maksutapaPoissa())
                required(polku.tositteenPvm)
                disabled(polku.tositteenPvm, () => this._tositteenPvmPoissa())
                required(polku.summa)
                min(polku.summa, -999999.99)
                validoiReaktiivisesti(polku.summa, FormValidators.eiNolla())
                max(polku.summa, 999999.99)
                disabled(polku.summa, () => this._summaPoissa())
                disabled(polku.alvVahennysoikeus, () => this._alvVahennysoikeusPoissa())
              },
              { injector: this._injector },
            )
            this.maksutapa = this.lomake.maksutapa
            this.tositteenPvm = this.lomake.tositteenPvm
            this.summa = this.lomake.summa
            this.toimittajanNimi = this.lomake.toimittajanNimi
            this.toimittajaAvain = this.lomake.toimittajaAvain
            this.uusiToimittaja = luoUusiToimittajaLomake(this._toimittajaTunnisteService)
            this.projektiAvain = this.lomake.projektiAvain
            this.lisatiedot = this.lomake.lisatiedot
            this.alvVahennysoikeus = this.lomake.alvVahennysoikeus
            const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

            muutokset(this.maksutapa)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.maksutapa = value
                this.setDisplayForSelectedMaksutapa(value)
              })

            // Delay value set to start first emit
            asetaArvo(this.maksutapa, this.kuitti.maksutapa)

            if (this.kuitti.x) {
              // If selvitettävä tosite, don't allow changing maksutapa, sum or date
              this.showMaksutavat = false
              this._maksutapaPoissa.set(true)
              this._summaPoissa.set(true)
              this._tositteenPvmPoissa.set(true)
              this._alvVahennysoikeusPoissa.set(true)
            }

            if (this.kuitti.maksutapa === SAHKOISET_LASKUT_MAKSUTAPA_ID) {
              // If sähköinen lasku, don't allow changing maksutapa, sum or date
              this.showMaksutavat = false
              this._maksutapaPoissa.set(true)
              this._summaPoissa.set(true)
              this._tositteenPvmPoissa.set(true)
              this._alvVahennysoikeusPoissa.set(true)
            }

            muutokset(this.tositteenPvm)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe((value: Date) => {
                this.kuitti.pvm = this._timestampService.dateToTimestamp(value)
                this.kuitti.localPvm = this._dateService.dateToLocalDate(value)
                this.kuitti.p = this._dateService.dateToNumber(value)
              })
            muutokset(this.summa)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.summa = Math.abs(value)
                this.kuitti.sum = value
              })
            muutokset(this.toimittajanNimi)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.seller = value
              })
            muutokset(this.toimittajaAvain)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                if (value) {
                  this.kuitti.toimittajaAvain = value
                } else {
                  delete this.kuitti.toimittajaAvain
                }
              })
            muutokset(this.projektiAvain)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.projektiAvain = value || null
              })
            muutokset(this.lisatiedot)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.selite = value
              })
            muutokset(this.alvVahennysoikeus)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(value => {
                this.kuitti.alvVahennysoikeus = value
              })

            this.kirjanpitajanKommentti = this.kuitti.m || ''
            this.maksajaTaiMaksunSaaja = this.kuitti.w || this.kuitti.el || ''
            this._changeDetectionRef.markForCheck()
          }
        },
        error: error => {
          this._errorHandler.handleError(error)
        },
      })

    this.donePercentageObservable = this._uploadTasksSubject.pipe(
      switchMap(tasks => {
        if (!tasks || tasks.length < 1) {
          return observableOf<number>(null)
        }
        return this._tiedostojenLataamisService.annaKokonaisprosenttiSimplified(tasks).pipe(
          tap(percentage => {
            if (percentage > 99.99) {
              this.virheviestiLatausOnKesken = null
            }
          }),
        )
      }),
    )
    this.latausvirheetObservable = this._uploadTasksSubject.pipe(switchMap(tasks => this._tiedostojenLataamisService.annaKaikkiVirheetSimplified(tasks)))

    this.alvPortaatObservable = combineLatest([arvonMuutokset(this.maksutapa, { injector: this._injector }).pipe(startWith(this.kuitti.maksutapa)), this._kayttajaService.kayttajaObservable]).pipe(
      map(([maksutapa, kayttaja]) => {
        // Only shown for ostotositteet
        if (maksutapa === TILIOTETOSITE_MAKSUTAPA_ID || maksutapa === MYYNTITOSITE_MAKSUTAPA_ID || maksutapa === PALKKATOSITE_MAKSUTAPA_ID) {
          return null
        }
        if (!kayttaja?.alvVahennysoikeusPortaat || !kayttaja.prosessinOsat) {
          return null
        }
        const prosessissaAlv =
          kayttaja.prosessinOsat.filter(a => {
            return a.tunniste === TositteenLisaysprosessinOsanTunniste.ALV_VAHENNYSOIKEUS
          }).length > 0
        if (!prosessissaAlv) {
          return null
        }
        const portaat: number[] = []

        let def: number | null = null
        for (const porras of kayttaja.alvVahennysoikeusPortaat) {
          if (!isNaN(porras.arvo)) {
            if (porras.oletus) {
              def = porras.arvo
            }
            portaat.push(porras.arvo)
          }
        }
        portaat.sort((a, b) => a - b)
        return portaat.length > 0 ? { portaat: portaat, oletusProsentti: def } : null
      }),
    )

    this.alvPortaatObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      portaatJaOletus => {
        this.oletusAlv = portaatJaOletus?.oletusProsentti
        this.alvPortaat = portaatJaOletus?.portaat
        if (this.oletusAlv && (this.kuitti.alvVahennysoikeus === null || this.kuitti.alvVahennysoikeus === undefined)) {
          asetaArvo(this.alvVahennysoikeus, this.oletusAlv)
        }
      },
      error => {
        this._errorHandler.handleError(error)
      },
    )
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  paivitaToimittajanNimi(nimi: string) {
    asetaArvo(this.toimittajanNimi, nimi)
  }

  paivitaToimittajaAvain(avain: string | null) {
    asetaArvo(this.toimittajaAvain, avain)
    this.toimittajaAvain().markAsTouched()
  }

  paivitaProjektiAvain(avain: string) {
    asetaArvo(this.projektiAvain, avain)
  }

  paivitaMaksutapa(maksutapa: number) {
    asetaArvo(this.maksutapa, maksutapa)
  }

  edellinenSivu() {
    if (this.nykyinenSivuIndeksi > 0) {
      this.nykyinenSivuIndeksi--
    }
  }

  seuraavaSivu() {
    if (this.nykyinenSivuIndeksi < this._sivutSubject.value.length - 1) {
      this.nykyinenSivuIndeksi++
    }
  }

  avaaTiedostonValinta() {
    this.fileInput?.nativeElement.click()
  }

  async fileChanged(event: Event) {
    const target: HTMLInputElement = event.target as HTMLInputElement
    if (!target?.files?.length) {
      return
    }
    const tiedostot = Array.from(target.files)
    target.value = ''
    await this.lisaaSivut(tiedostot)
  }

  async lisaaSivut(tiedostot: File[]) {
    this.virheviestiEiKuvia = null
    const sallitut = await this._tiedostotSallitut(tiedostot)
    if (!sallitut) {
      return
    }

    const kayttajanTiedot = this.kayttajanTiedot ?? (await this._kayttajaService.getKayttajanTiedot())

    for (const tiedosto of tiedostot) {
      const sivu = this._luoUusiSivu(tiedosto, kayttajanTiedot)
      this._sivutSubject.next([...this._sivutSubject.value, sivu])
      this.nykyinenSivuIndeksi = this._sivutSubject.value.length - 1
      this._uploadTasksSubject.next([...this._uploadTasksSubject.value, sivu.uploadData])
      this._kasitteleSivunTiedosto(sivu, tiedosto, kayttajanTiedot)
    }
  }

  poistaSivuKlikattu(sivuIndeksi: number, event: Event) {
    event.stopPropagation()
    const sivut = this._sivutSubject.value
    const sivu = sivut[sivuIndeksi]
    if (!sivu || sivu.lukittu) {
      return
    }

    if (sivu.uusi) {
      this.newAlkuperaisetCache = this.newAlkuperaisetCache.filter(a => a.avain !== sivu.alkuperaisenAvain)
      this.newKuvatCache = this.newKuvatCache.filter(k => !sivu.kuvaAvaimet.includes(k.avain))
      if (this.kuitti.alkuperaiset) {
        delete this.kuitti.alkuperaiset[sivu.alkuperaisenAvain]
      }
      if (this.kuitti.kuvat) {
        for (const kuvaAvain of sivu.kuvaAvaimet) {
          delete this.kuitti.kuvat[kuvaAvain]
        }
      }
      this._uploadTasksSubject.next(this._uploadTasksSubject.value.filter(u => u !== sivu.uploadData))
    } else {
      this._poistetuksiMerkitytSivut.push(sivu.alkuperaisenAvain)
    }

    if (sivuIndeksi <= this.nykyinenSivuIndeksi && this.nykyinenSivuIndeksi > 0) {
      this.nykyinenSivuIndeksi--
    }
    this._sivutSubject.next(sivut.filter((_, i) => i !== sivuIndeksi))
  }

  naytaSuurempana() {
    const sivu = this._sivutSubject.value[this.nykyinenSivuIndeksi]
    if (sivu?.uusi && sivu.esikatselukuva) {
      this.naytaModal = true
    }
  }

  suljeModal() {
    this.naytaModal = false
  }

  private _rakennaSivutOlemassaolevasta(kayttajanTiedot: KayttajanTiedot, tosite: FirestoreTosite): MuokattavaSivu[] {
    const ryhmat = new Map<string, FirestoreTositteenKuva[]>()
    for (const kuva of Object.values(tosite.kuvat || {})) {
      if (kuva.poistettu) {
        continue
      }
      const avain = kuva.alkuperaisenAvain || kuva.avain
      if (!ryhmat.has(avain)) {
        ryhmat.set(avain, [])
      }
      ryhmat.get(avain).push(kuva)
    }

    const sivut: MuokattavaSivu[] = []
    for (const [alkuperaisenAvain, kuvat] of ryhmat) {
      kuvat.sort((a, b) => a.jarjestys - b.jarjestys)
      const alkuperainen = tosite.alkuperaiset?.[alkuperaisenAvain]
      const onLukittu = !!alkuperainen?.lukittu || kuvat.some(k => !!k.lukittu)
      sivut.push({
        alkuperaisenAvain: alkuperaisenAvain,
        nimi: alkuperainen?.nimi || '',
        esikatselukuva: '',
        kuvaUrlit: kuvat.map(k => this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, tosite, k, 'full')),
        kuvaAvaimet: kuvat.map(k => k.avain),
        uploadData: null,
        uusi: false,
        lukittu: onLukittu,
      })
    }
    sivut.sort((a, b) => (tosite.kuvat[a.kuvaAvaimet[0]]?.jarjestys ?? 0) - (tosite.kuvat[b.kuvaAvaimet[0]]?.jarjestys ?? 0))
    return sivut
  }

  private _luoUusiSivu(tiedosto: File, kayttajanTiedot: KayttajanTiedot): MuokattavaSivu {
    const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFile(tiedosto)
    const alkuperainen: FirestoreTositteenAlkuperainenTiedosto = {
      avain: this._firebaseLemonaid.firestoreCreateId(),
      kuvakansio: this.kuitti.kuvakansio,
      nimi: tiedosto.name,
      fileEnding: fileEnding,
      kasitelty: false,
    }
    this.newAlkuperaisetCache.push(alkuperainen)
    if (!this.kuitti.alkuperaiset) {
      this.kuitti.alkuperaiset = {}
    }
    this.kuitti.alkuperaiset[alkuperainen.avain] = alkuperainen

    const uploadData = this._tiedostojenLataamisService.tallennaTositetiedostoSimplified(this._firebaseLemonaid, kayttajanTiedot.asiakasId + '', alkuperainen, tiedosto)

    return {
      alkuperaisenAvain: alkuperainen.avain,
      nimi: tiedosto.name,
      esikatselukuva: '',
      kuvaUrlit: [],
      kuvaAvaimet: [],
      uploadData: uploadData,
      uusi: true,
      lukittu: false,
    }
  }

  private async _kasitteleSivunTiedosto(sivu: MuokattavaSivu, tiedosto: File, kayttajanTiedot: KayttajanTiedot) {
    this._luoEsikatselukuva(tiedosto, sivu.alkuperaisenAvain)

    try {
      const fileDropEntry = new FileDropEntry(tiedosto.name, null)
      const alkuperainen = this.kuitti.alkuperaiset[sivu.alkuperaisenAvain]
      const purettu: PurettuTiedosto = await this._tositeService.puraYksiTiedostoLokaalisti(
        this.kuitti,
        tiedosto,
        fileDropEntry,
        alkuperainen.fileEnding,
        sivu.uploadData,
        alkuperainen,
        kayttajanTiedot,
        false,
        Promise.resolve({}),
      )

      if (purettu.virhe) {
        this.virheviestiPysyva = this.virheviestiPysyva ? this.virheviestiPysyva + ' ' + purettu.virhe : purettu.virhe
      }
      if (purettu.kasitellytKuvat) {
        const jarjestetytKuvat = [...purettu.kasitellytKuvat].sort((a, b) => a.jarjestys - b.jarjestys)
        for (const kuva of jarjestetytKuvat) {
          this.newKuvatCache.push(kuva)
          if (!this.kuitti.kuvat) {
            this.kuitti.kuvat = {}
          }
          this.kuitti.kuvat[kuva.avain] = kuva
          sivu.kuvaAvaimet.push(kuva.avain)
        }
        const kuvaUrlit = jarjestetytKuvat.map(k => this._tositeKuvaCacheService.annaKuvanUrl(kayttajanTiedot, this.kuitti, k, 'full'))
        const paivitetyt = this._sivutSubject.value.map(s => (s.alkuperaisenAvain === sivu.alkuperaisenAvain ? { ...s, kuvaUrlit } : s))
        this._sivutSubject.next(paivitetyt)
      }
      this._changeDetectionRef.markForCheck()
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }

  private _luoEsikatselukuva(tiedosto: File, sivunAvain: string) {
    if (tiedosto.type === 'application/pdf') {
      return
    }
    const lukija = new FileReader()
    lukija.onload = e => {
      if (!e.target?.result) {
        return
      }
      const paivitetyt = this._sivutSubject.value.map(s => (s.alkuperaisenAvain === sivunAvain ? { ...s, esikatselukuva: e.target.result as string } : s))
      this._sivutSubject.next(paivitetyt)
      this._changeDetectionRef.markForCheck()
    }
    lukija.readAsDataURL(tiedosto)
  }

  private async _tiedostotSallitut(tiedostot: File[]): Promise<boolean> {
    this.kuvavirheAvain = ''
    this.kuvavirheParametrit = {}

    const supportedImageTypes = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'pdf', 'heic', 'heif']
    for (const tiedosto of tiedostot) {
      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromFile(tiedosto)
      if (!fileEnding || supportedImageTypes.indexOf(fileEnding.toLowerCase()) < 0) {
        this.kuvavirheParametrit = { tuetutMuodot: supportedImageTypes.join(', ') }
        this.kuvavirheAvain = 'kuitit.lataaminen.virheet.vaara-muoto'
        return false
      }
      if (tiedosto.size === 0) {
        this.kuvavirheParametrit = { tiedostonNimi: tiedosto.name }
        this.kuvavirheAvain = 'kuitit.lataaminen.virheet.tyhja'
        return false
      }
      const maxKoko = 25 * 1024 * 1024
      if (tiedosto.size > maxKoko) {
        const kokoMegatavuissaLokalisoitu = this._currencyService.formatoiDesimaali(tiedosto.size / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        const maxKokoLokalisoitu = this._currencyService.formatoiDesimaali(maxKoko / 1024 / 1024, 2, this._translationService.nykyinenKieli)
        this.kuvavirheParametrit = { kokoMax: maxKokoLokalisoitu, kokoNyt: kokoMegatavuissaLokalisoitu }
        this.kuvavirheAvain = 'kuitit.lataaminen.virheet.liian-suuri'
        return false
      }
    }
    return true
  }

  private _saveInFlight: boolean
  async save() {
    if (this._saveInFlight) {
      return
    }
    this._saveInFlight = true
    this.virheviestiTallennus = null

    const kelpoisuus = () => this._onkoLomakeKelvollinen()
    try {
      const onkoTallentaminenEstetty = await this._tositeService.saveIsPreventedSimplified(
        // Palvelu lukee lomakkeelta vain kelpoisuuden
        {
          get valid() {
            return kelpoisuus()
          },
        } as Parameters<TositeService['saveIsPreventedSimplified']>[0],
        this.latausvirheetObservable,
        this._uploadTasksSubject.value,
        this.virheviestiPysyva,
      )
      if (onkoTallentaminenEstetty && onkoTallentaminenEstetty.tallennusEstetty) {
        if (onkoTallentaminenEstetty.lataaminenKesken) {
          this.virheviestiLatausOnKesken = this._translationService.lokalisoi('kuitit.muokkaa.validation.ladataan')
        }
        this._naytaTallennuksenEstot()
        return
      }
      if (!this.onkoLisattaviaKuvia()) {
        this.virheviestiEiKuvia = this._translationService.lokalisoi('kuitit.muokkaa.validation.ei-kuvia')
        this._naytaTallennuksenEstot()
        return
      }

      this.tallennusEstyi = false
      this._ladataanService.aloitaLataaminen()

      const tositeOliSelvitettava = this.kuitti.x

      const vastaus = await kutsuTositteenTallennusta<WebTositeTallennaPyynto, WebTositeTallennaVastaus>(this._firebaseLemonaid, 'tositeTallennaWeb', this._luoTallennuspyynto())
      if (vastaus.e === 'locked') {
        this._dialog.open(TositeLukittuDialog)
        return
      }
      if (vastaus.e) {
        console.error(`Tositteen ${this.kuitti.avain} muokkaus epäonnistui: ${vastaus.e}`, vastaus.puuttuvat)
        this.virheviestiTallennus = this._translationService.lokalisoi('tositteet.yhteiset.tallentaminen-epaonnistui')
        return
      }

      if (tositeOliSelvitettava && this.onkoVielaSelvitettaviaListalla) {
        await this._tositeService.setLastViewedKuitinMaksutapa(null)
        const prevLocation = this._tositeService.getMaksutapaNameForRouter(this.startingMaksutapa)
        this._router.navigate(['tositteet', prevLocation])
      } else {
        await this._tositeService.setLastViewedKuitinMaksutapa(this.kuitti.maksutapa)
        this._tositeDatasourceUusiService.changeVuosiKk(this.kuitti.localPvm.year, this.kuitti.localPvm.month - 1)
        const targetLocation = this._tositeService.getMaksutapaNameForRouter(this.kuitti.maksutapa)
        this._router.navigate(['tositteet', targetLocation])
      }
    } catch (error) {
      this.virheviestiTallennus = this._translationService.lokalisoi('tositteet.yhteiset.tallentaminen-epaonnistui')
      this._errorHandler.handleError(error)
    } finally {
      this._saveInFlight = false
      this._ladataanService.lopetaLataaminen()
      this._changeDetectionRef.markForCheck()
    }
  }

  get naytaTallennuksenEsto() {
    return this.tallennusEstyi && (this._onkoLomakeVirheellinen() || !!this.virheviestiEiKuvia || !!this.virheviestiLatausOnKesken || !!this.virheviestiPysyva || !!this.kuvavirheAvain)
  }

  private _naytaTallennuksenEstot() {
    this.tallennusEstyi = true
    this.lomake().markAsTouched()
    this.uusiToimittaja.markAllAsTouched()
    this._changeDetectionRef.markForCheck()
    setTimeout(() => {
      document.querySelector<HTMLElement>('main.page .mat-form-field-invalid, main.page mat-error, main.page .error-text')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  private _luoTallennuspyynto(): WebTositeTallennaPyynto {
    const sivut = this._sivutSubject.value
    // Säilyvät sivut pitävät vanhan järjestysnumeronsa, joten uudet numeroidaan niiden perään
    const suurinSailyvaJarjestys = Math.max(0, ...sivut.filter(sivu => !sivu.uusi).flatMap(sivu => sivu.kuvaAvaimet.map(avain => this.kuitti.kuvat?.[avain]?.jarjestys ?? 0)))
    const pyynto: WebTositeTallennaPyynto = {
      avain: this.kuitti.avain,
      uusi: false,
      p: this.kuitti.p ?? this._dateService.dateToNumber(this._dateService.timestampToDate(this.kuitti.pvm)),
      sum: this.kuitti.sum ?? this.kuitti.summa,
      selite: this.kuitti.selite ?? '',
      maksutapa: this.kuitti.maksutapa,
      seller: this.kuitti.seller ?? '',
      toimittajaAvain: this.uusiToimittaja.enabled ? null : (this.kuitti.toimittajaAvain ?? null),
      projektiAvain: this.kuitti.projektiAvain || null,
      kuvakansio: this.kuitti.kuvakansio,
      lisatytKuvat: sivut
        .filter(sivu => sivu.uusi)
        .map((sivu, indeksi) => {
          const alkuperainen = this.newAlkuperaisetCache.find(a => a.avain === sivu.alkuperaisenAvain)
          return { avain: alkuperainen.avain, nimi: alkuperainen.nimi, tiedostopaate: alkuperainen.fileEnding, jarjestys: suurinSailyvaJarjestys + 1 + indeksi }
        }),
      poistetutKuvat: [...this._poistetuksiMerkitytSivut],
    }
    if (this.kuitti.alvVahennysoikeus !== null && this.kuitti.alvVahennysoikeus !== undefined) {
      pyynto.alvVahennysoikeus = this.kuitti.alvVahennysoikeus
    }
    const uusiToimittaja = uusiToimittajaLomakkeesta(this.uusiToimittaja)
    if (uusiToimittaja) {
      pyynto.uusiToimittaja = uusiToimittaja
    }
    if (this._selvityspyynnonAvain) {
      pyynto.selvityspyynnonVastaus = { avain: this._selvityspyynnonAvain, vastaus: this._translationService.lokalisoi('tositteet.selvitys.tositetta-muokattu') }
    }
    return pyynto
  }

  async peruuta() {
    this._poistetuksiMerkitytSivut = []

    if (!this.kuitti.x) {
      await this._tositeService.setLastViewedKuitinMaksutapa(this.startingMaksutapa)
    } else {
      await this._tositeService.setLastViewedKuitinMaksutapa(null)
    }
    this._tositeDatasourceUusiService.changeVuosiKk(this.startingMonth.year, this.startingMonth.month - 1)
    const targetLocation = this._tositeService.getMaksutapaNameForRouter(this.startingMaksutapa)
    this._router.navigate(['tositteet', targetLocation])
  }

  private setDisplayForSelectedMaksutapa(maksutapaId: number) {
    if (maksutapaId === TILIOTETOSITE_MAKSUTAPA_ID) {
      this.showPvm = true
      this.pvmText = this._translationService.lokalisoi('tositteet.tiliote.muokkaa.pvm')
      this.hideSum = true
      this._summaPoissa.set(true)
      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'month'
    } else if (maksutapaId === PALKKATOSITE_MAKSUTAPA_ID) {
      this.showPvm = false
      this.pvmText = this._translationService.lokalisoi('tositteet.palkka.muokkaa.pvm')
      this.hideSum = true
      this.dateSelectionType = 'day'
      this._summaPoissa.set(true)
      this._tositteenPvmPoissa.set(true)
    } else if (maksutapaId === MYYNTITOSITE_MAKSUTAPA_ID) {
      this.showPvm = true
      this.pvmText = this._translationService.lokalisoi('tositteet.myynti.muokkaa.pvm')
      this.summaText = this._translationService.lokalisoi('tositteet.myynti.muokkaa.summa')
      this._summaPoissa.set(false)
      this.hideSum = false
      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'day'
    } else {
      this.showPvm = true
      this.pvmText = this._translationService.lokalisoi('kuitit.muokkaa.maksupvm')
      this.summaText = this._translationService.lokalisoi('kuitit.muokkaa.summa')
      this._summaPoissa.set(false)
      this.hideSum = false
      this._tositteenPvmPoissa.set(false)
      this.dateSelectionType = 'day'
    }
  }

  virheet<T>(kentta: FieldTree<T>): { [laji: string]: any } | null {
    const virheet = kentta().errors()
    return virheet.length ? Object.fromEntries(virheet.map(virhe => [virhe.kind, virhe])) : null
  }

  private _onkoLomakeKelvollinen() {
    return this.lomake().valid() && (this.uusiToimittaja.disabled || this.uusiToimittaja.valid)
  }

  private _onkoLomakeVirheellinen() {
    return this.lomake().invalid() || this.uusiToimittaja.invalid
  }

  private onkoLisattaviaKuvia() {
    return this._sivutSubject.value.length > 0
  }
}
